import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Input, Select, Typography } from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import TextArea from 'antd/es/input/TextArea';
import { mobileRegex } from 'utils/regex';

import { Spinner, TitledRoundedCard } from 'components';

import { ENQUIRY_TYPE, EnquiryTypeItemType } from '../utils/enquiryTypes';
import { useProfileInfoContext } from '../../../../contexts/profileInfo';
import styled from 'styled-components';

const { Text } = Typography;

const StyleFormItem = styled(Form.Item)`
  max-width: 400px;

  textarea {
    height: 120px !important;
  }
`;

export const EnquiryInfoCard = () => {
  const { t } = useTranslation();

  const [requiresMailOrPhone, setRequiresMailOrPhone] = useState(false);

  const form = useFormInstance();
  const phone = Form.useWatch('phone', form);
  const mail = Form.useWatch('mail', form);

  const { state: data, isLoading } = useProfileInfoContext();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: `${data.firstName} ${data.lastName}`,
        mail: data.email,
        phone: data.mobile,
      });
    }
  }, [data, form]);

  const enquiryTypesArray = Object.keys(ENQUIRY_TYPE);
  const enquiryTypesObject: EnquiryTypeItemType[] = enquiryTypesArray.map((enquiryType) => ({
    label: t(ENQUIRY_TYPE[enquiryType]),
    value: enquiryType,
  }));

  useEffect(() => {
    setRequiresMailOrPhone(!mail);
    if (form.isFieldTouched('phone') || form.isFieldTouched('mail')) {
      form.validateFields(['phone', 'mail']);
    }
  }, [form, mail, phone]);

  return (
    <TitledRoundedCard title={t('enquiry.enquiryInfo')} numbering="1">
      {isLoading && <Spinner />}
      <StyleFormItem rules={[{ required: true }]} label={<Text>{t('enquiry.nameLabel')}</Text>} name="name">
        <Input disabled />
      </StyleFormItem>
      <StyleFormItem
        rules={[
          { required: requiresMailOrPhone, message: t('enquiry.error.contactError') },
          { type: 'email', message: t('common.error.incorrectInformation') },
        ]}
        required
        label={<Text>{t('enquiry.emailLabel')}</Text>}
        name="mail"
      >
        <Input placeholder={t('enquiry.emailPlaceholder')} />
      </StyleFormItem>
      <StyleFormItem
        rules={[
          // { required: requiresMailOrPhone, message: t('enquiry.error.contactError') },
          () => ({
            validator(_, value) {
              if (!value || mobileRegex.test(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('registration.error.invalidMobile')));
            },
          }),
        ]}
        required
        label={<Text>{t('enquiry.phoneLabel')}</Text>}
        name="phone"
      >
        <Input placeholder={t('enquiry.phonePlaceholder')} />
      </StyleFormItem>
      <StyleFormItem
        rules={[{ required: true, message: t('enquiry.error.typeError') }]}
        required
        label={<Text>{t('enquiry.typeLabel')}</Text>}
        name="enquiryType"
      >
        <Select placeholder={t('enquiry.typePlaceholder')}>
          {enquiryTypesObject &&
            enquiryTypesObject.map((item) => (
              <Select.Option key={item.value} value={item.value}>
                {item.label}
              </Select.Option>
            ))}
        </Select>
      </StyleFormItem>
      <StyleFormItem
        rules={[{ required: true, message: t('enquiry.error.enquiryMessageError') }]}
        required
        label={<Text>{t('enquiry.enquiryMessageLabel')}</Text>}
        name="feedback"
      >
        <TextArea showCount maxLength={500} placeholder={t(`enquiry.enquiryMessagePlaceholder`)} />
      </StyleFormItem>
    </TitledRoundedCard>
  );
};
